/**
 * Native haptic feedback for the Capacitor shell.
 *
 * Thin, intent-named wrappers around @capacitor/haptics so callers don't couple
 * to the plugin and every call is uniformly guarded. Each is a hard no-op
 * outside the Capacitor shell — importantly, that means mobile-web and MobiLoud
 * users get NO vibration (we never reach the plugin's web `navigator.vibrate`
 * fallback), so this can't introduce buzzing on the responsive site.
 *
 * Fire-and-forget and never throws: a failed/absent haptic must never interrupt
 * the action it accompanies.
 *
 * @capacitor/haptics is loaded lazily (dynamic import) so it is excluded from
 * the main entry bundle and never downloaded/parsed in MobiLoud or plain web —
 * only fetched when isCapacitorEnvironment() is true.
 */

import Honeybadger from "@honeybadger-io/js";
import { isCapacitorEnvironment } from "./detection";

function reportHapticError(error) {
  try {
    Honeybadger.notify(error, {
      context: { component: "capacitor_haptics" },
    });
  } catch {
    // reporting must never break the app
  }
}

// Run a haptic only inside the shell, swallowing any error (incl. a synchronous
// throw) so it can never disrupt the user action it's reinforcing.
function runHaptic(fn) {
  if (!isCapacitorEnvironment()) {
    return;
  }
  import("@capacitor/haptics").then(fn).catch(reportHapticError);
}

/** Light tap when a set is marked complete — the core lifting loop. */
export function hapticSetComplete() {
  runHaptic(({ Haptics, ImpactStyle }) =>
    Haptics.impact({ style: ImpactStyle.Light }),
  );
}

/** Light tap when a chat message is successfully sent. */
export function hapticMessageSent() {
  runHaptic(({ Haptics, ImpactStyle }) =>
    Haptics.impact({ style: ImpactStyle.Light }),
  );
}

/** Success notification when a whole workout is marked complete. */
export function hapticWorkoutComplete() {
  runHaptic(({ Haptics, NotificationType }) =>
    Haptics.notification({ type: NotificationType.Success }),
  );
}

/** Notification buzz when a rest-timer countdown reaches zero. */
export function hapticRestComplete() {
  runHaptic(({ Haptics, NotificationType }) =>
    Haptics.notification({ type: NotificationType.Success }),
  );
}
